export enum State {
  New = 0,
  Learning = 1,
  Review = 2,
  Relearning = 3,
}

export enum Rating {
  Again = 1,
  Hard = 2,
  Good = 3,
  Easy = 4,
}

export const DEFAULT_DESIRED_RETENTION = 0.9;
export const MIN_DESIRED_RETENTION = 0.7;
export const MAX_DESIRED_RETENTION = 0.97;

export const resolveDesiredRetention = (value: unknown): number => {
  if (!Number.isFinite(value)) return DEFAULT_DESIRED_RETENTION;

  return Math.min(
    Math.max(value as number, MIN_DESIRED_RETENTION),
    MAX_DESIRED_RETENTION
  );
};

export class ReviewLog {
  rating: Rating;
  scheduledDays: number;
  elapsedDays: number;
  review: Date;
  state: State;
  constructor(
    rating: Rating,
    scheduledDays: number,
    elapsedDays: number,
    review: Date,
    state: State
  ) {
    this.rating = rating;
    this.elapsedDays = elapsedDays;
    this.scheduledDays = scheduledDays;
    this.review = review;
    this.state = state;
  }
}

export class Card {
  due: Date | string;
  stability: number;
  difficulty: number;
  elapsedDays: number;
  scheduledDays: number;
  reps: number;
  lapses: number;
  state: State;
  lastReview: Date | string;
  difficultyIndex: number;
  sessionId?: string;
  constructor() {
    this.due = new Date();
    this.stability = 0;
    this.difficulty = 0;
    this.elapsedDays = 0;
    this.scheduledDays = 0;
    this.reps = 0;
    this.lapses = 0;
    this.state = State.New;
    this.lastReview = new Date();
    this.difficultyIndex = 0;
  }
}

export class SchedulingInfo {
  card: Card;
  review_log: ReviewLog;

  constructor(card: Card, review_log: ReviewLog) {
    this.card = card;
    this.review_log = review_log;
  }
}

class SchedulingCards {
  again: Card;
  hard: Card;
  good: Card;
  easy: Card;

  constructor(card: Card) {
    this.again = { ...card };
    this.hard = { ...card };
    this.good = { ...card };
    this.easy = { ...card };
  }

  updateState(state: State) {
    if (state === State.New) {
      this.again.state = State.Learning;
      this.hard.state = State.Learning;
      this.good.state = State.Learning;
      this.easy.state = State.Review;
    } else if (state === State.Learning || state === State.Relearning) {
      this.again.state = state;
      this.hard.state = state;
      this.good.state = State.Review;
      this.easy.state = State.Review;
    } else if (state === State.Review) {
      this.again.state = State.Relearning;
      this.hard.state = State.Review;
      this.good.state = State.Review;
      this.easy.state = State.Review;
      this.again.lapses += 1;
    }
  }

  schedule(
    now: Date,
    hardInterval: number,
    goodInterval: number,
    easyInterval: number
  ) {
    this.again.scheduledDays = 0;
    this.hard.scheduledDays = hardInterval;
    this.good.scheduledDays = goodInterval;
    this.easy.scheduledDays = easyInterval;

    this.again.due = new Date(now.getTime() + 5 * 60 * 1000);
    if (hardInterval > 0) {
      this.hard.due = new Date(
        now.getTime() + hardInterval * 24 * 60 * 60 * 1000
      );
    } else {
      this.hard.due = new Date(now.getTime() + 10 * 60 * 1000);
    }

    this.good.due = new Date(
      now.getTime() + goodInterval * 24 * 60 * 60 * 1000
    );

    this.easy.due = new Date(
      now.getTime() + easyInterval * 24 * 60 * 60 * 1000
    );
  }

  record_log(card: Card, now: Date): Record<Rating, SchedulingInfo> {
    return {
      [Rating.Again]: new SchedulingInfo(
        this.again,
        new ReviewLog(
          Rating.Again,
          this.again.scheduledDays,
          card.elapsedDays,
          now,
          card.state
        )
      ),
      [Rating.Hard]: new SchedulingInfo(
        this.hard,
        new ReviewLog(
          Rating.Hard,
          this.hard.scheduledDays,
          card.elapsedDays,
          now,
          card.state
        )
      ),
      [Rating.Good]: new SchedulingInfo(
        this.good,
        new ReviewLog(
          Rating.Good,
          this.good.scheduledDays,
          card.elapsedDays,
          now,
          card.state
        )
      ),
      [Rating.Easy]: new SchedulingInfo(
        this.easy,
        new ReviewLog(
          Rating.Easy,
          this.easy.scheduledDays,
          card.elapsedDays,
          now,
          card.state
        )
      ),
    };
  }
}

class Params {
  request_retention: number;
  maximum_interval: number;
  w: Array<number>;

  constructor(requestRetention?: number) {
    this.request_retention = resolveDesiredRetention(requestRetention);
    this.maximum_interval = 36500;

    this.w = [
      0.212, 1.2931, 2.3065, 8.2956, 6.4133, 0.8334, 3.0194, 0.001, 1.8722,
      0.1666, 0.796, 1.4835, 0.0614, 0.2629, 1.6483, 0.6014, 1.8729, 0.5425,
      0.0912, 0.0658, 0.1542,
    ];
  }
}

export class FSRS {
  p: Params;
  DECAY: number;
  FACTOR: number;

  constructor(options?: { requestRetention?: number }) {
    this.p = new Params(options?.requestRetention);
    this.DECAY = -this.p.w[20];
    this.FACTOR = Math.pow(0.9, 1 / this.DECAY) - 1;
  }

  repeat(card: Card, now: Date): Record<number, SchedulingInfo> {
    card = { ...card };

    // Sanitize corrupt persisted scheduling before it drives the math. A
    // non-finite stability/difficulty or an invalid `lastReview` otherwise
    // cascades into NaN intervals, producing an invalid `due` Date whose
    // `.toISOString()` throws "Invalid time value" at the call site.
    // Note: `NaN <= 0` is false, so the finite check must come first.
    if (!Number.isFinite(card.stability) || card.stability <= 0) {
      console.warn(`Fixing card with invalid stability: ${card.stability}`);

      card.stability = 0.1;
    }

    if (!Number.isFinite(card.difficulty)) {
      console.warn(`Fixing card with invalid difficulty: ${card.difficulty}`);

      card.difficulty = this.init_difficulty(Rating.Good);
    }

    if (card.state === State.New) {
      card.elapsedDays = 0;
    } else {
      const lastReviewMs = new Date(card.lastReview).getTime();

      card.elapsedDays = Number.isFinite(lastReviewMs)
        ? (now.getTime() - lastReviewMs) / 86400000
        : 0;
    }
    card.lastReview = now;
    card.reps += 1;
    const s = new SchedulingCards(card);
    s.updateState(card.state);

    if (card.state === State.New) {
      this.init_ds(s);
      s.again.due = new Date(now.getTime() + 60 * 1000); // 1 minute
      s.hard.due = new Date(now.getTime() + 5 * 60 * 1000); // 5 minutes
      s.good.due = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 1 day
      const easyInterval = this.next_interval(s.easy.stability);
      s.easy.scheduledDays = easyInterval;
      s.easy.due = new Date(now.getTime() + easyInterval * 24 * 60 * 60 * 1000);
    } else if (
      card.state === State.Learning ||
      card.state === State.Relearning
    ) {
      const last_d = card.difficulty;
      const last_s = card.stability;
      s.again.difficulty = this.next_difficulty(last_d, Rating.Again);
      s.again.stability = this.next_short_term_stability(last_s, Rating.Again);
      s.hard.difficulty = this.next_difficulty(last_d, Rating.Hard);
      s.hard.stability = this.next_short_term_stability(last_s, Rating.Hard);
      s.good.difficulty = this.next_difficulty(last_d, Rating.Good);
      s.good.stability = this.next_short_term_stability(last_s, Rating.Good);
      s.easy.difficulty = this.next_difficulty(last_d, Rating.Easy);
      s.easy.stability = this.next_short_term_stability(last_s, Rating.Easy);

      const hardInterval = 0;
      const goodInterval = this.next_interval(s.good.stability);

      const easyInterval = Math.max(
        this.next_interval(s.easy.stability),
        goodInterval + 1
      );

      s.schedule(now, hardInterval, goodInterval, easyInterval);
    } else if (card.state === State.Review) {
      const interval = card.elapsedDays;
      const last_d = card.difficulty;
      const last_s = card.stability;
      const retrievability = this.forgetting_curve(interval, last_s);
      this.next_ds(s, last_d, last_s, retrievability);

      let hardInterval = this.next_interval(s.hard.stability);
      let goodInterval = this.next_interval(s.good.stability);
      hardInterval = Math.min(hardInterval, goodInterval);
      goodInterval = Math.max(goodInterval, hardInterval + 1);

      const easyInterval = Math.max(
        this.next_interval(s.easy.stability),
        goodInterval + 1
      );

      s.schedule(now, hardInterval, goodInterval, easyInterval);
    }
    return s.record_log(card, now);
  }

  init_ds(s: SchedulingCards): void {
    s.again.difficulty = this.init_difficulty(Rating.Again);
    s.again.stability = this.init_stability(Rating.Again);
    s.hard.difficulty = this.init_difficulty(Rating.Hard);
    s.hard.stability = this.init_stability(Rating.Hard);
    s.good.difficulty = this.init_difficulty(Rating.Good);
    s.good.stability = this.init_stability(Rating.Good);
    s.easy.difficulty = this.init_difficulty(Rating.Easy);
    s.easy.stability = this.init_stability(Rating.Easy);
  }

  next_ds(
    s: SchedulingCards,
    last_d: number,
    last_s: number,
    retrievability: number
  ): void {
    s.again.difficulty = this.next_difficulty(last_d, Rating.Again);

    s.again.stability = this.next_forget_stability(
      last_d,
      last_s,
      retrievability
    );

    s.hard.difficulty = this.next_difficulty(last_d, Rating.Hard);

    s.hard.stability = this.next_recall_stability(
      last_d,
      last_s,
      retrievability,
      Rating.Hard
    );

    s.good.difficulty = this.next_difficulty(last_d, Rating.Good);

    s.good.stability = this.next_recall_stability(
      last_d,
      last_s,
      retrievability,
      Rating.Good
    );

    s.easy.difficulty = this.next_difficulty(last_d, Rating.Easy);

    s.easy.stability = this.next_recall_stability(
      last_d,
      last_s,
      retrievability,
      Rating.Easy
    );
  }

  init_stability(r: Rating): number {
    return Math.max(this.p.w[r - 1], 0.1);
  }

  init_difficulty(r: Rating): number {
    return this.constrain_difficulty(
      this.p.w[4] - Math.exp(this.p.w[5] * (r - 1)) + 1
    );
  }

  forgetting_curve(elapsed_days: number, stability: number): number {
    return Math.pow(1 + (this.FACTOR * elapsed_days) / stability, this.DECAY);
  }

  next_interval(stability: number): number {
    const new_interval =
      (stability / this.FACTOR) *
      (Math.pow(this.p.request_retention, 1 / this.DECAY) - 1);

    return Math.min(
      Math.max(Math.round(new_interval), 1),
      this.p.maximum_interval
    );
  }

  constrain_difficulty(difficulty: number): number {
    return Math.min(Math.max(+difficulty.toFixed(2), 1), 10);
  }

  linear_damping(delta_d: number, old_d: number): number {
    return (delta_d * (10 - old_d)) / 9;
  }

  next_difficulty(d: number, rating: Rating): number {
    const delta_d = -this.p.w[6] * (rating - 3);
    const next_d = d + this.linear_damping(delta_d, d);
    return this.constrain_difficulty(
      this.mean_reversion(this.init_difficulty(Rating.Easy), next_d)
    );
  }

  mean_reversion(init: number, current: number): number {
    return this.p.w[7] * init + (1 - this.p.w[7]) * current;
  }

  next_recall_stability(
    d: number,
    s: number,
    r: number,
    rating: Rating
  ): number {
    const hardPenalty = rating === Rating.Hard ? this.p.w[15] : 1;
    const easyBonus = rating === Rating.Easy ? this.p.w[16] : 1;

    const newStability =
      s *
      (1 +
        Math.exp(this.p.w[8]) *
          (11 - d) *
          Math.pow(s, -this.p.w[9]) *
          (Math.exp((1 - r) * this.p.w[10]) - 1) *
          hardPenalty *
          easyBonus);

    return +Math.max(newStability, 0.1).toFixed(2);
  }

  next_forget_stability(d: number, s: number, r: number): number {
    const sMin = s / Math.exp(this.p.w[17] * this.p.w[18]);

    const newStability = Math.min(
      this.p.w[11] *
        Math.pow(d, -this.p.w[12]) *
        (Math.pow(s + 1, this.p.w[13]) - 1) *
        Math.exp((1 - r) * this.p.w[14]),
      sMin
    );

    return +Math.max(newStability, 0.1).toFixed(2);
  }

  next_short_term_stability(s: number, rating: Rating): number {
    let sinc =
      Math.exp(this.p.w[17] * (rating - 3 + this.p.w[18])) *
      Math.pow(s, -this.p.w[19]);

    if (rating >= 3) {
      sinc = Math.max(sinc, 1);
    }
    const newStability = s * sinc;
    // Add minimum stability guard
    return +Math.max(newStability, 0.1).toFixed(2);
  }
}
